import React, { useMemo } from 'react';
import { Badge, Button, Form, Stack } from 'react-bootstrap';
import {
  EVENT_KIND_ORDER,
  formatEventTimestamp,
  getEventKindCounts,
  getEventKindDefinition,
  getUniqueEventKinds,
  hasValidEventLatLng,
} from './eventHistoryUtils';

const sortKinds = (kinds) => {
  const order = new Map(EVENT_KIND_ORDER.map((key, index) => [key, index]));
  return kinds.slice().sort((a, b) => {
    const aIndex = order.has(a) ? order.get(a) : 999;
    const bIndex = order.has(b) ? order.get(b) : 999;
    if (aIndex !== bIndex) return aIndex - bIndex;
    return a.localeCompare(b);
  });
};

export default function EventHistoryMapToolsPanel({
  events = [],
  selectedKinds = new Set(),
  selectedEventId = null,
  onToggleKind,
  onClearFilters,
  onClearSelection,
  lastUpdated = null,
}) {
  const mappableEvents = useMemo(
    () => (Array.isArray(events) ? events.filter((event) => hasValidEventLatLng(event)) : []),
    [events],
  );
  const kinds = useMemo(() => sortKinds(getUniqueEventKinds(mappableEvents)), [mappableEvents]);
  const counts = useMemo(() => getEventKindCounts(mappableEvents), [mappableEvents]);
  const allowedKinds = selectedKinds instanceof Set ? selectedKinds : new Set(selectedKinds || []);
  const noneSelected = allowedKinds.size === 0;

  return (
    <div className="event-map-tools-panel p-3">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <div className="fw-semibold">Map Events</div>
          <div className="small text-muted">Filter map markers by type.</div>
        </div>
        {lastUpdated && (
          <div className="small text-muted text-end">
            Updated
            <br />
            {formatEventTimestamp(lastUpdated)}
          </div>
        )}
      </div>

      {kinds.length ? (
        <Stack gap={2}>
          {kinds.map((kind) => {
            const definition = getEventKindDefinition(kind);
            const Icon = definition.Icon;
            return (
              <div key={kind} className="d-flex align-items-center justify-content-between">
                <Form.Check
                  type="checkbox"
                  id={`event-filter-${kind}`}
                  label={
                    <span className="d-flex align-items-center gap-2">
                      <span className="event-history-icon" style={{ background: definition.color }}>
                        {Icon && <Icon size={14} />}
                      </span>
                      <span>{definition.label}</span>
                    </span>
                  }
                  checked={allowedKinds.has(kind)}
                  onChange={() => onToggleKind?.(kind)}
                />
                {counts[kind] ? (
                  <Badge bg="secondary" pill>
                    {counts[kind]}
                  </Badge>
                ) : null}
              </div>
            );
          })}
        </Stack>
      ) : (
        <div className="small text-muted">No mappable events yet.</div>
      )}

      <div className="d-flex align-items-center gap-2 mt-3">
        <Button
          size="sm"
          variant="outline-light"
          onClick={onClearFilters}
          disabled={noneSelected || !kinds.length}
        >
          Clear Filters
        </Button>
        <Button
          size="sm"
          variant="outline-info"
          onClick={onClearSelection}
          disabled={!selectedEventId}
        >
          Clear Focus
        </Button>
      </div>
    </div>
  );
}
